/**
 * 「今天」——「夜自习」的主场。
 *
 * 焦点顺序是刻意排的：**最紧的那个倒计时**占满第一屏顶部，用全屏最亮的奶油色，
 * 视线先落在它上面；接着是今日进度环与那颗开始/结束按钮（他唯一要动手的地方）；
 * 日历退到第三位——它是证据，不是主角；次要里程碑压成一行小胶囊。
 *
 * 参照 Spotify 的结构语言：内容优先的暗场、胶囊控件、卡片靠提亮分层而非描边。
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import { useWakeLock } from '@pwaship/react'
import PushCard from '../components/PushCard'
import Tasks from '../components/Tasks'
import { newClock, liveSecs, commit, onTick, onHidden, onVisible, addBack, restore, ASK_MS, type Clock } from '../lib/timer'
import { studyApi, fmtClock, type CalDay, type Milestone, type StudyMode, type TodayState } from '../lib/study'

const BEAT_MS = 15_000
const WEEK = ['一', '二', '三', '四', '五', '六', '日']
const monthKey = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
const dayKey = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`

export default function Today() {
  const [month, setMonth] = useState(() => monthKey(new Date()))
  const [days, setDays] = useState<Record<string, CalDay>>({})
  const [marks, setMarks] = useState<Record<string, Milestone[]>>({})
  const [upcoming, setUpcoming] = useState<Milestone[]>([])
  const [today, setToday] = useState<TodayState | null>(null)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    const [c, u, t] = await Promise.all([studyApi.calendar(month), studyApi.upcoming(4), studyApi.today()])
    if (c.ok) {
      setDays(Object.fromEntries(c.data.days.map((d) => [d.day, d])))
      const m: Record<string, Milestone[]> = {}
      for (const x of c.data.milestones) (m[x.day] ??= []).push(x)
      setMarks(m)
    }
    if (u.ok) setUpcoming(u.data.milestones)
    if (t.ok) setToday(t.data); else setError(t.message)
  }, [month])

  useEffect(() => { void load() }, [load])

  if (error) {
    return <div style={{ padding: 32, textAlign: 'center', color: 'var(--ps-text2)' }}>读不到学习记录：{error}</div>
  }

  return (
    <div className="safe-b" style={{
      display: 'grid', gap: 14, padding: '8px 16px 28px',
      maxWidth: 520, margin: '0 auto', width: '100%',
    }}>
      <Greeting t={today} />
      <Tasks />
      <PushCard />
      <Hero m={upcoming[0]} />
      <div className="rise rise-2"><Timer today={today} onChanged={load} /></div>
      <div className="rise rise-3"><Calendar month={month} days={days} marks={marks} onMonth={setMonth} /></div>
      <div className="rise rise-4"><Upnext list={upcoming.slice(1)} /></div>
    </div>
  )
}

/* ── 今天学什么 ─────────────────────────────────
   有了排期表之后，「120 分钟」不再是一个孤零零的数字 —— 它由几个具体的科目块组成。
   不摊开写，Carson 打开只会看到一个总数，然后不知道该先做什么。 */
function PlanStrip({ t }: { t: TodayState | null }) {
  if (!t) return null
  const hasPlan = t.plan.length > 0
  return (
    <div style={{ width: '100%', display: 'grid', gap: 10 }}>
      {hasPlan ? (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, justifyContent: 'center' }}>
          {t.plan.map((p) => (
            <span key={p.name} style={{
              display: 'inline-flex', alignItems: 'baseline', gap: 6,
              padding: '8px 13px', borderRadius: 12,
              background: 'var(--ps-surface2)', border: '1px solid var(--ps-line)',
              fontSize: 14.5, fontWeight: 700, color: 'var(--ps-text)',
            }}>
              {p.name}
              <b className="num" style={{ fontSize: 15, color: 'var(--ps-accent)' }}>{p.minutes}</b>
              <span style={{ fontSize: 12.5, color: 'var(--ps-text3)', fontWeight: 600 }}>分钟</span>
            </span>
          ))}
        </div>
      ) : (
        <p style={{ margin: 0, textAlign: 'center', fontSize: 13.5, color: 'var(--ps-text3)' }}>
          今天没有排计划
        </p>
      )}
      {t.bonusMinutes > 0 && (
        /* 补偿必须写清楚从哪来 —— 目标数字无缘无故变大，只会让人觉得这 App 在耍他 */
        <p style={{
          margin: 0, textAlign: 'center', fontSize: 13, lineHeight: 1.6,
          color: 'var(--cal-missed-fg)', fontWeight: 700,
        }}>
          上周欠了 {t.debtMinutes} 分钟 · 今天补 {t.bonusMinutes} 分钟（{t.baseMinutes} → {t.targetMinutes}）
        </p>
      )}
    </div>
  )
}

/* ── 页头：日期 + 连击 + 每日一句 ─────────────────
   替代原来只写着「今天」的标题栏。那条栏占了一整行高度却不带任何信息，
   而每天第一眼该看到的是「今天几号、连了几天、今天这句话」。 */
function Greeting({ t }: { t: TodayState | null }) {
  const now = new Date()
  const wd = '日一二三四五六'[now.getDay()]
  return (
    <header className="rise rise-1" style={{ display: 'grid', gap: 10, padding: '10px 2px 2px' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
        <span className="num" style={{
          fontSize: 15, fontWeight: 800, color: 'var(--ps-text2)', letterSpacing: '.02em',
        }}>{now.getMonth() + 1} 月 {now.getDate()} 日 · 周{wd}</span>
        {!!t?.streak && t.streak > 0 && (
          <span style={{
            padding: '3px 10px', borderRadius: 999, fontSize: 12.5, fontWeight: 800,
            background: 'var(--cal-done-bg)', color: 'var(--cal-done-fg)',
          }}>连续达标 {t.streak} 天</span>
        )}
      </div>
      {/* 每日中二一句。取自服务端，按日期做种子 —— 刷新不换，换来换去就没人当真了 */}
      <p style={{
        margin: 0, fontSize: 22, lineHeight: 1.5, fontWeight: 800,
        letterSpacing: '-.01em', color: 'var(--ps-text)', textWrap: 'balance',
      }}>
        <span style={{ color: 'var(--ps-tint)', marginRight: 2 }}>「</span>
        {t?.quote?.text ?? '\u00a0'}
        <span style={{ color: 'var(--ps-tint)', marginLeft: 2 }}>」</span>
      </p>
    </header>
  )
}

/* ── 主角：最紧的那个倒计时 ───────────────────── */
function Hero({ m }: { m?: Milestone }) {
  if (!m) {
    return (
      <div className="rise rise-1" style={{ ...card, padding: '26px 20px', textAlign: 'center', color: 'var(--ps-text3)' }}>
        还没有排考期
      </div>
    )
  }
  const d = m.daysLeft ?? 0
  const urgent = d <= 30
  return (
    <section className="rise rise-1" style={{
      ...card, position: 'relative', overflow: 'hidden',
      padding: '22px 20px 20px',
      background: urgent
        ? 'linear-gradient(160deg, var(--ps-accent-soft), var(--ps-surface) 62%)'
        : 'var(--ps-surface)',
      border: `1px solid ${urgent ? 'var(--ps-accent-line)' : 'var(--ps-line)'}`,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 2 }}>
        <span style={{
          fontSize: 14, letterSpacing: '.08em',
          color: urgent ? 'var(--ps-accent)' : 'var(--ps-text3)', fontWeight: 700,
        }}>{m.kind === 'exam' ? '下一场考试' : m.kind === 'mock' ? '下一次模考' : '下一个检查点'}</span>
        {urgent && <span className="pulsing" style={{ width: 6, height: 6, borderRadius: 99, background: 'var(--ps-accent)' }} />}
      </div>

      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 10, marginTop: 6 }}>
        <span className="num" style={{
          fontSize: 'clamp(76px, 26vw, 112px)', fontWeight: 900, lineHeight: .8,
          letterSpacing: '-.05em',
          color: urgent ? 'var(--ps-text)' : 'var(--ps-text)',
        }}>{d}</span>
        <span style={{ fontSize: 19, fontWeight: 700, color: 'var(--ps-text2)', paddingBottom: 10 }}>天后</span>
        <span className="num" style={{
          marginLeft: 'auto', fontSize: 14, color: 'var(--ps-text3)', paddingBottom: 12,
        }}>{m.day}</span>
      </div>

      <div style={{ marginTop: 12, fontSize: 24, fontWeight: 800, letterSpacing: '-.02em' }}>
        {m.title}
        {m.targetScore && (
          <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--ps-accent)', marginLeft: 8 }}>
            目标 {m.targetScore}{m.targetStretch ? `–${m.targetStretch}` : ''}
          </span>
        )}
      </div>
    </section>
  )
}

/* ── 开始前的模式选择 ─────────────────────────────
   浏览器分不清「锁屏」和「切到别的 App」—— 两者都只是 visibilitychange 变成
   hidden，没有任何 API 能区分。所以只能由本人在开始时声明这一场打算怎么学；
   任何启发式（比如按隐藏时长猜）都会在真实使用里判错，而判错的代价是
   一整段学习时间凭空消失，那比不计时更让人恼火。

   刻意用整块面板而不是系统弹窗：JS 的 confirm 会冻住页面，而且在 PWA 里很丑。 */
function ModePicker({ onPick, onCancel, busy }: {
  onPick(m: StudyMode): void; onCancel(): void; busy: boolean
}) {
  const opts: { m: StudyMode; emoji: string; name: string; desc: string }[] = [
    { m: 'onscreen', emoji: '📱', name: '专注模式', desc: '就在手机上学。切到别的 App 立刻暂停，防自己摸鱼' },
    { m: 'offscreen', emoji: '📖', name: '离屏模式', desc: '看纸质书、用平板、听音频。锁屏切走都照常计，全靠自觉' },
  ]
  return (
    <div style={{ width: '100%', display: 'grid', gap: 10 }}>
      <p style={{ margin: '0 2px', fontSize: 14.5, fontWeight: 800, color: 'var(--ps-text2)' }}>
        这一场怎么学？
      </p>
      {opts.map((o) => (
        <button key={o.m} onClick={() => onPick(o.m)} disabled={busy} style={{
          width: '100%', textAlign: 'left', cursor: 'pointer',
          background: 'var(--ps-surface2)', border: '1.5px solid var(--ps-line)',
          borderRadius: 16, padding: '14px 15px', display: 'flex', gap: 12, alignItems: 'flex-start',
        }}>
          <span style={{ fontSize: 22, lineHeight: 1.2 }} aria-hidden>{o.emoji}</span>
          <span style={{ flex: 1, minWidth: 0 }}>
            <span style={{ display: 'block', fontSize: 16.5, fontWeight: 800, color: 'var(--ps-text)' }}>{o.name}</span>
            <span style={{ display: 'block', fontSize: 13.5, lineHeight: 1.6, color: 'var(--ps-text2)', marginTop: 3 }}>
              {o.desc}
            </span>
          </span>
        </button>
      ))}
      <button onClick={onCancel} disabled={busy} style={{
        minHeight: 44, borderRadius: 999, border: 'none', background: 'none', cursor: 'pointer',
        fontSize: 14.5, fontWeight: 700, color: 'var(--ps-text3)',
      }}>先不开始</button>
    </div>
  )
}

/* ── 今日进度环 + 计时 ────────────────────────── */
function Timer({ today, onChanged }: { today: TodayState | null; onChanged(): void }) {
  const [sessionId, setSessionId] = useState<number | null>(null)
  const [gapMs, setGapMs] = useState(0)
  const [busy, setBusy] = useState(false)
  const [msg, setMsg] = useState<string | null>(null)
  const [mode, setMode] = useState<StudyMode>('onscreen')
  const [choosing, setChoosing] = useState(false)
  const [, tick] = useState(0)                 // 每秒逼一次重绘，时间本身存在 ref 里

  const wake = useWakeLock()

  /* 计时账目全部在 lib/timer.ts 里，纯函数、可单测。
     核心取舍写在那个文件的头注释里：只计入「亲眼看着走过」的时间。 */
  const clockRef = useRef<Clock>(newClock(Date.now(), false))
  const liveRef = useRef(0)

  const live = liveSecs(clockRef.current, Date.now())
  liveRef.current = live
  // 必须带 running 守卫：没有会话时 segStart 本来就是 null，
  // 否则「已切走」那行提示会在没开始计时的时候常驻
  const paused = sessionId !== null && clockRef.current.segStart === null

  // 服务端是否已经在某次 today 里确认过这个会话。
  // 没有这个标记就不能走「在别处结束了」那条路 —— 刚 start 完的那一瞬间，
  // 父组件手上的 today 还是旧的（open 为 null），会立刻把自己刚建的会话清掉。
  const confirmedRef = useRef(false)

  /* 恢复 / 跨设备同步 */
  useEffect(() => {
    if (!today) return
    if (today.open && sessionId === null) {
      const m = today.open.mode ?? 'onscreen'
      clockRef.current = restore(today.open, m, Date.now(), document.visibilityState === 'visible')
      setMode(m)
      setSessionId(today.open.id)
      return
    }
    // 会话在别的设备上结束了，这一台要跟着收尾，否则会永远停在「结束学习」上
    if (today.open?.id === sessionId) confirmedRef.current = true
    if (!today.open && sessionId !== null && confirmedRef.current) {
      clockRef.current = newClock(Date.now(), false)
      confirmedRef.current = false
      setSessionId(null); setGapMs(0)
      void wake.release()
      setMsg('这次学习已在别的设备上结束')
    }
  }, [today, sessionId, wake])

  /* 主时钟。既驱动重绘，也是「切走了没有」的唯一判据 ——
     页面被挂起时定时器一定停，心跳迟到就说明这段时间没在跑。 */
  useEffect(() => {
    if (sessionId === null) return
    clockRef.current = { ...clockRef.current, lastTick: Date.now() }
    const t = setInterval(() => {
      const r = onTick(clockRef.current, Date.now(), mode)
      clockRef.current = r.clock
      if (r.frozenMs >= ASK_MS) setGapMs(r.frozenMs)   // 几秒的小空档照样不计入，只是不打扰人
      tick((n) => n + 1)
    }, 1000)
    return () => clearInterval(t)
  }, [sessionId, mode])

  /* 心跳上报。不再把暂停状态放进依赖，避免每次暂停/恢复都重建定时器 */
  useEffect(() => {
    if (sessionId === null) return
    const t = setInterval(() => {
      void studyApi.beat(sessionId, liveRef.current, clockRef.current.segStart === null)
    }, BEAT_MS)
    return () => clearInterval(t)
  }, [sessionId])

  /* 可见性只是让界面反应更快。即使这个事件一次都不触发，时长也是对的 ——
     那条保证由上面的冻结检测提供，lib/timer.ts 里有对应的回归测试。 */
  /* 离屏模式回到前台时也必须补心跳 —— 它才是最需要的那个场景：
     手机锁着的整段时间心跳都是断的，回来慢一步就会被服务端判超时。 */
  useEffect(() => {
    if (sessionId === null || mode !== 'offscreen') return
    const onVis = () => {
      if (document.visibilityState !== 'visible') return
      clockRef.current = { ...clockRef.current, lastTick: Date.now() }
      void studyApi.beat(sessionId, liveSecs(clockRef.current, Date.now()), false)
      tick((n) => n + 1)
    }
    document.addEventListener('visibilitychange', onVis)
    return () => document.removeEventListener('visibilitychange', onVis)
  }, [sessionId, mode])

  useEffect(() => {
    if (sessionId === null || mode === 'offscreen') return
    const onVis = () => {
      const now = Date.now()
      if (document.visibilityState === 'hidden') {
        clockRef.current = onHidden(clockRef.current, now, mode)
      } else {
        const r = onVisible(clockRef.current, now, mode)
        clockRef.current = r.clock
        if (r.awayMs >= ASK_MS) setGapMs(r.awayMs)
        // 回到前台立刻补一次心跳，不等下一个 BEAT_MS。
        // 页面在后台时定时器被冻结，心跳是断的；服务端只看心跳判超时，
        // 慢一步就可能在下一次 /today 里被 closeStale 砍掉。
        void studyApi.beat(sessionId, liveSecs(clockRef.current, now), r.clock.segStart === null)
        void wake.acquire()   // 锁在不可见时会被系统收走，回来必须重申
      }
      tick((n) => n + 1)
    }
    document.addEventListener('visibilitychange', onVis)
    return () => document.removeEventListener('visibilitychange', onVis)
  }, [sessionId, wake, mode])

  const start = async (m: StudyMode) => {
    setBusy(true); setMsg(null); setGapMs(0); setChoosing(false)
    const r = await studyApi.start(m)
    setBusy(false)
    if (!r.ok) {
      if (/已有进行中/.test(r.message)) { setMsg('另一台设备已经在计时了，这里接着显示'); onChanged(); return }
      return setMsg(r.message)
    }
    setMode(m)
    clockRef.current = newClock(Date.now())
    confirmedRef.current = false
    setSessionId(r.data.id)
    onChanged()          // 让父组件的 today 立刻带上这个会话，别停在旧快照上
    // 常亮锁只有专注模式要：让自动锁屏不发生，从而少一种误判来源。
    // 离屏模式本来就不看可见性，还占着常亮纯属白耗电。
    if (m === 'onscreen') void wake.acquire()
  }

  const stop = async () => {
    if (sessionId === null) return
    setBusy(true)
    const final = commit(clockRef.current, Date.now())
    const r = await studyApi.end(sessionId, Math.floor(final.accumMs / 1000))
    setBusy(false); void wake.release()
    clockRef.current = newClock(Date.now(), false)
    confirmedRef.current = false
    setSessionId(null); setGapMs(0)
    if (r.ok) {
      setMsg(r.data.status === 'done' ? `目标达成，今天 ${r.data.minutes} 分钟 🎉`
        : `今天 ${r.data.minutes} / ${r.data.targetMinutes} 分钟`)
      onChanged()
    } else setMsg(r.message)
  }

  const target = today?.targetMinutes ?? 0
  const totalMin = (today?.minutes ?? 0) + Math.floor(live / 60)
  const pct = target > 0 ? Math.min(1, totalMin / target) : 0
  const running = sessionId !== null

  // 环放大到 224：46px 的 00:03 在 210 的环里内切宽度不够，长会话「1:02:30」直接压到环上。
  // 内半径 = 100 - 16/2 = 92，在 ±32 高度处可用半宽 86px，够放 7 位时钟（38px 时约 ±80）。
  const R = 100, C = 2 * Math.PI * R, SIZE = 224
  const clock = fmtClock(live)
  const clockSize = clock.length >= 7 ? 38 : 46
  return (
    <section style={{ ...card, padding: '22px 20px 20px', display: 'grid', gap: 18, justifyItems: 'center' }}>
      <div style={{ position: 'relative', width: SIZE, height: SIZE }}>
        <svg width={SIZE} height={SIZE} viewBox={`0 0 ${SIZE} ${SIZE}`} style={{ transform: 'rotate(-90deg)' }} aria-hidden>
          <circle cx={SIZE / 2} cy={SIZE / 2} r={R} fill="none" stroke="var(--ps-line)" strokeWidth="16" />
          <circle
            cx={SIZE / 2} cy={SIZE / 2} r={R} fill="none"
            stroke={paused ? 'var(--cal-missed-fg)' : 'var(--ps-accent)'}
            strokeWidth="16" strokeLinecap="round"
            strokeDasharray={C} strokeDashoffset={C * (1 - pct)}
            style={{ transition: 'stroke-dashoffset .6s cubic-bezier(.2,.8,.3,1), stroke .3s' }}
          />
        </svg>
        {/* 原来用 grid + placeItems:center：两个子元素各占一行，各自在自己那条行带里居中，
            于是数字被顶高、标签被压到环底 —— 截图里「计时中」被环切掉就是这么来的。
            改成 flex 纵向居中，整组作为一个块居中。 */}
        <div style={{
          position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center', gap: 8, textAlign: 'center',
        }}>
          {running ? (
            <>
              {/* 紧凑时钟 00:03，不用「0 分 03 秒」—— 中文写法太宽会撑出圆环 */}
              <span className="num" style={{
                fontSize: clockSize, fontWeight: 900, letterSpacing: '-.04em', lineHeight: 1,
                color: paused ? 'var(--cal-missed-fg)' : 'var(--ps-accent)',
                fontVariantNumeric: 'tabular-nums',
              }}>{clock}</span>
              <span style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                padding: '5px 12px', borderRadius: 999,
                /* 原来用日历的 done 配色对：#5C8629 底 + #0F130C 字 = 4.38:1，
                   13px 粗体不算大字号，卡在 AA 的 4.5 之下。换成 accent/on-accent。 */
                /* 暂停态不能照抄实心：--cal-missed-fg 在深色主题下是浅鲑色 #FF8A7A，
                   拿它当底色配白字只有 2:1。实心留给「计时中」，暂停用描边。 */
                background: paused ? 'transparent' : 'var(--ps-accent)',
                border: paused ? '1.5px solid var(--cal-missed-fg)' : 'none',
                color: paused ? 'var(--cal-missed-fg)' : 'var(--ps-on-accent)',
                fontSize: 13, fontWeight: 800, letterSpacing: '.04em',
              }}>
                <i className={paused ? '' : 'beat'} style={{
                  width: 7, height: 7, borderRadius: 999, background: 'currentColor', display: 'block',
                }} />
                {paused ? '已暂停' : '计时中'}
              </span>
            </>
          ) : (
            <>
              <span className="num" style={{
                fontSize: 54, fontWeight: 900, letterSpacing: '-.05em', lineHeight: 1,
                fontVariantNumeric: 'tabular-nums',
              }}>{totalMin}</span>
              <span style={{ fontSize: 14, color: 'var(--ps-text3)', fontWeight: 700 }}>今日 / 目标 {target} 分钟</span>
            </>
          )}
        </div>
      </div>

      <PlanStrip t={today} />

      {/* 结束态原先用 surface2 做底，在浅色主题下和卡片几乎同色 —— 按钮整个看不见了。
          改成描边胶囊：和实心的「开始」明显不同，但仍是一颗看得见的按钮。 */}
      {choosing ? (
        <ModePicker onPick={start} onCancel={() => setChoosing(false)} busy={busy} />
      ) : (
        <button onClick={running ? stop : () => setChoosing(true)} disabled={busy} style={{
          width: '100%', minHeight: 58, borderRadius: 999, cursor: 'pointer',
          background: running ? 'transparent' : 'var(--ps-accent)',
          border: running ? '2px solid var(--ps-accent)' : 'none',
          color: running ? 'var(--ps-accent)' : 'var(--ps-on-accent)',
          fontSize: 18, fontWeight: 800, letterSpacing: '.02em',
          boxShadow: running ? 'none' : 'var(--ps-shadow-accent)',
        }}>{running ? '结束学习' : '开始学习'}</button>
      )}

      {!running && today?.targetSource === 'default' && (
        <span style={{ fontSize: 13.5, color: 'var(--ps-text3)' }}>目标时长待学习计划确定</span>
      )}
      {running && !paused && gapMs === 0 && (
        <span style={{ fontSize: 11.5, color: 'var(--ps-text3)', textAlign: 'center', lineHeight: 1.6 }}>
          {mode === 'onscreen'
            ? '专注模式 · 屏幕保持常亮，切到别的 App 会自动停'
            : '离屏模式 · 锁屏、切走都照常计时，靠自觉'}
        </span>
      )}
      {paused && (
        <span style={{ fontSize: 14, color: 'var(--cal-missed-fg)', fontWeight: 600 }}>已切走，回到这一页自动继续</span>
      )}

      {gapMs > 0 && (
        <div style={{
          width: '100%', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
          padding: '10px 12px', borderRadius: 14,
          background: 'var(--ps-accent-soft)', border: '1px solid var(--ps-accent-line)',
        }}>
          <span style={{ fontSize: 12.5 }}>刚才中断了 {Math.round(gapMs / 60000)} 分钟</span>
          {/* 一键补回：直接改学习记录，只在人确认「刚才确实在学」时才动。
              加进已确认累计，而不是伪造一个更早的段起点 —— 后者会被下一次冻结检测再扣一遍。 */}
          <button onClick={() => { clockRef.current = addBack(clockRef.current, gapMs); setGapMs(0) }} style={{
            marginLeft: 'auto', padding: '6px 14px', borderRadius: 999, cursor: 'pointer',
            border: 'none', background: 'var(--ps-accent)', color: 'var(--ps-on-accent)',
            fontSize: 12.5, fontWeight: 700,
          }}>算上</button>
          <button onClick={() => setGapMs(0)} style={{
            padding: '6px 10px', borderRadius: 999, cursor: 'pointer',
            border: 'none', background: 'transparent', color: 'var(--ps-text3)', fontSize: 12.5,
          }}>不算</button>
        </div>
      )}

      {msg && <span role="status" style={{ fontSize: 13, color: 'var(--ps-text2)' }}>{msg}</span>}
    </section>
  )
}

/* ── 日历 ─────────────────────────────────────── */
function Calendar({ month, days, marks, onMonth }: {
  month: string; days: Record<string, CalDay>; marks: Record<string, Milestone[]>; onMonth(m: string): void
}) {
  const [y, mo] = month.split('-').map(Number) as [number, number]
  const total = new Date(y, mo, 0).getDate()
  const lead = (new Date(y, mo - 1, 1).getDay() + 6) % 7
  const todayStr = dayKey(new Date())
  const shift = (d: number) => onMonth(monthKey(new Date(y, mo - 1 + d, 1)))
  const cells: (string | null)[] = [
    ...Array(lead).fill(null),
    ...Array.from({ length: total }, (_, i) => `${month}-${String(i + 1).padStart(2, '0')}`),
  ]
  const doneCount = Object.values(days).filter((d) => d.status === 'done').length

  return (
    <section style={{ ...card, padding: '16px 14px 14px' }}>
      <header style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 12, padding: '0 2px' }}>
        <button onClick={() => shift(-1)} aria-label="上个月" style={ghost}>‹</button>
        <strong style={{ fontSize: 18, fontWeight: 800 }}>{mo} 月</strong>
        <button onClick={() => shift(1)} aria-label="下个月" style={ghost}>›</button>
        <span style={{ marginLeft: 'auto', fontSize: 14, color: 'var(--ps-text3)' }}>
          达标 <b className="num" style={{ color: 'var(--ps-accent)', fontWeight: 800, fontSize: 17 }}>{doneCount}</b> 天
        </span>
      </header>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', gap: 5 }}>
        {WEEK.map((w) => (
          <div key={w} style={{ textAlign: 'center', fontSize: 12.5, color: 'var(--ps-text4)', paddingBottom: 6 }}>{w}</div>
        ))}
        {cells.map((d, i) => <Cell key={d ?? `x${i}`} day={d} info={d ? days[d] : undefined}
          marks={d ? marks[d] : undefined} isToday={d === todayStr} />)}
      </div>

      <div style={{ display: 'flex', gap: 16, marginTop: 16, fontSize: 13, color: 'var(--ps-text3)', paddingLeft: 2 }}>
        <Dot c="var(--cal-done-bg)" t="达标" />
        <Dot c="var(--cal-partial-bg)" b="var(--cal-partial-line)" t="部分" />
        <Dot c="var(--cal-missed-bg)" b="var(--cal-missed-line)" t="没学" />
      </div>
    </section>
  )
}

function Dot({ c, b, t }: { c: string; b?: string; t: string }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
      <i style={{ width: 11, height: 11, borderRadius: 4, background: c, border: b ? `1px solid ${b}` : 'none' }} />{t}
    </span>
  )
}

function Cell({ day, info, marks, isToday }: {
  day: string | null; info?: CalDay; marks?: Milestone[]; isToday: boolean
}) {
  if (!day) return <div />
  const st = info?.status ?? 'idle'
  const bg = st === 'done' ? 'var(--cal-done-bg)' : st === 'partial' ? 'var(--cal-partial-bg)'
    : st === 'missed' ? 'var(--cal-missed-bg)' : 'transparent'
  const fg = st === 'done' ? 'var(--cal-done-fg)' : st === 'missed' ? 'var(--cal-missed-fg)'
    : st === 'idle' ? 'var(--ps-text4)' : 'var(--ps-text)'
  const line = st === 'partial' ? 'var(--cal-partial-line)' : st === 'missed' ? 'var(--cal-missed-line)' : 'transparent'
  const hint = [day, info ? `${info.minutes} / ${info.targetMinutes} 分钟` : '无记录',
    ...(marks ?? []).map((m) => `${m.kind === 'exam' ? '考试' : '模考'}：${m.title}`)].join('\n')

  return (
    <div title={hint} style={{
      aspectRatio: '1', borderRadius: 12, background: bg, border: `1px solid ${line}`,
      outline: isToday ? '2px solid var(--ps-accent)' : 'none', outlineOffset: -2,
      display: 'grid', placeItems: 'center', position: 'relative',
      fontSize: 15, fontWeight: st === 'done' ? 800 : 600, color: fg,
      fontFamily: 'var(--font-num)', fontVariantNumeric: 'tabular-nums',
    }}>
      {Number(day.slice(-2))}
      {marks && marks.length > 0 && (
        <span aria-label="里程碑" style={{
          position: 'absolute', bottom: 3, width: 4, height: 4, borderRadius: 99,
          background: marks.some((m) => m.kind === 'exam') ? 'var(--cal-missed-fg)' : 'var(--ps-accent)',
        }} />
      )}
    </div>
  )
}

/* ── 次要里程碑：压成一行胶囊 ─────────────────── */
function Upnext({ list }: { list: Milestone[] }) {
  if (!list.length) return null
  return (
    <section style={{ display: 'grid', gap: 8 }}>
      <span style={{ fontSize: 11, letterSpacing: '.14em', textTransform: 'uppercase', color: 'var(--ps-text3)', fontWeight: 700, paddingLeft: 4 }}>
        再往后
      </span>
      <div style={{ display: 'flex', gap: 8, overflowX: 'auto', paddingBottom: 4, scrollbarWidth: 'none' }}>
        {list.map((m) => (
          <div key={m.day + m.title} style={{
            flex: '0 0 auto', display: 'flex', alignItems: 'baseline', gap: 8,
            padding: '12px 18px', borderRadius: 999,
            background: 'var(--ps-surface)', border: '1px solid var(--ps-line)',
          }}>
            <span style={{ fontSize: 15.5, fontWeight: 700 }}>{m.title}</span>
            <span className="num" style={{ fontSize: 19, fontWeight: 900, color: 'var(--ps-accent)' }}>{m.daysLeft}</span>
            <span style={{ fontSize: 13, color: 'var(--ps-text3)' }}>天</span>
          </div>
        ))}
      </div>
    </section>
  )
}

const card: React.CSSProperties = {
  background: 'var(--ps-surface)', borderRadius: 20,
  border: '1px solid var(--ps-line)', boxShadow: 'var(--ps-shadow-sm)',
}
const ghost: React.CSSProperties = {
  width: 30, height: 30, borderRadius: 999, cursor: 'pointer', padding: 0,
  border: 'none', background: 'var(--ps-surface2)', color: 'var(--ps-text2)',
  fontSize: 17, lineHeight: 1, display: 'grid', placeItems: 'center',
}
